import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { GridContentWidget, PageTabsMainTabGridSize } from '@eforall/dashboard';
import { Event } from '@interfaces';
import { DomainDataService } from '../../../../../../services';
import { AcceleratorAreaService } from '../../../accelerator-area.service';
import { AccDeliveryService } from '../../acc-delivery.service';
import { getSetup } from './grid/get-setup';

export interface SurveysByTopicRow {
	accSessionTopicSurveyResponseId: number,
	accSessionAttendeeId: number,
	topicId: number,
	eventId: number,
	event: Event,
	sessionStartUTC: number,
	accVentureId: number,
	ventureName: string,
	entrepreneurPersonId: number,
	entrepreneurName: string,
	topicNames: string,
	topic: string,
	relevanceBucket: string,
	needBucket: string,
	understandingBucket: string,
	durationBucket: string,
	other: string | undefined,
	topicSurveyRelevanceScore: number,
	topicSurveyNeedScore: number,
	topicSurveyUnderstandingScore: number,
	topicSurveyDurationScore: number,
	createdUTC: number,
	updatedUTC: number,
}

@Component({
	selector: 'acc-surveys-by-topic-view-part',
	standalone: true,
	imports: [GridContentWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './surveys-by-topic-view.part.html',
})
export class AccSurveysByTopicViewPart {

	private readonly util = inject(UtilityService);
	private readonly pageService = inject(AccDeliveryService);
	private readonly areaService = inject(AcceleratorAreaService);
	private readonly domain = inject(DomainDataService);

	public setup = getSetup(this.util);

	public pageTabsGridSize = computed<PageTabsMainTabGridSize | undefined>(() => {
		if (this.pageService.layout()) return { layout: this.pageService.layout()!, viewSelector: true };
		return undefined;
	});

	public surveysByTopic = computed<SurveysByTopicRow[]>(() => {
		const data = this.areaService.data();
		if (!data) return [];


		const events = this.areaService.data()?.events ?? [];

		const ventures = data.ventures;
		const sessions = data.sessions;
		const sessionTopics = data.sessionTopics;
		const accTopics = data.accTopics;
		const sessionAttendees = data.sessionAttendees;
		const sessionSurveyResults = data.sessionSurveyResponses;
		const sessionTopicSurveyResults = data.topicSurveyResponses;

		const ddTopics = this.domain.data().topics;

		return sessionTopicSurveyResults.map(survey => {

			const sessionSurveyResponse = sessionSurveyResults.find(s => s.accSessionSurveyResponseId == survey.accSessionSurveyResponseId)!;

			const event = events.find(e => e.eventId == sessionSurveyResponse.eventId)!;
			const accSession = sessions.find(e => e.eventId == event.eventId)!;

			//
			// Get the attendee records for this session
			//
			const attendee = sessionAttendees.find(a => a.accSessionAttendeeId == sessionSurveyResponse.accSessionAttendeeId && !!a.accVentureId)!;

			const accVenture = ventures.find(venture => venture.accVentureId == attendee.accVentureId)!;

			//
			// Topics
			//
			const accSessionTopics = sessionTopics.filter(sessionTopic => sessionTopic.accSessionId == accSession.accSessionId);
			const accTopicIds = accSessionTopics.map(topic => topic.accTopicId);
			const sessionTopicIds = accTopics.filter(topic => accTopicIds.includes(topic.accTopicId)).map(topic => topic.topicId);


			const row: SurveysByTopicRow = {
				eventId: sessionSurveyResponse.eventId,
				event,
				topicNames: sessionTopicIds.length ? ddTopics.filter(topic => sessionTopicIds.includes(topic.topicId)).map(topic => topic.name).sort((a, b) => a > b ? 1 : -1).join(', ') : '',
				accSessionAttendeeId: sessionSurveyResponse.accSessionAttendeeId,
				accSessionTopicSurveyResponseId: survey.accSessionTopicSurveyResponseId,
				topicId: survey.topicId,
				topic: ddTopics.find(topic => topic.topicId == survey.topicId)?.name || '',
				sessionStartUTC: event.startUTC,
				accVentureId: accVenture.accVentureId,
				ventureName: accVenture.ventureName,
				entrepreneurPersonId: attendee.personId,
				entrepreneurName: attendee.personName,
				durationBucket: this.util.nps.getBucket(survey.duration),
				needBucket: this.util.nps.getBucket(survey.need),
				relevanceBucket: this.util.nps.getBucket(survey.relevance),
				understandingBucket: this.util.nps.getBucket(survey.understanding),
				other: survey.other,
				createdUTC: sessionSurveyResponse.createdUTC,
				topicSurveyDurationScore: survey.duration,
				topicSurveyNeedScore: survey.need,
				topicSurveyRelevanceScore: survey.relevance,
				topicSurveyUnderstandingScore: survey.understanding,
				updatedUTC: survey.updatedUTC
			};

			return row;
		});

	});
}